import { profileTakesAttachments } from '@commenda/api/commenda-ai/api';
import { COMMENDA_CHAT_PROFILE_KEY } from '@commenda/api/commenda-ai/constants';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import type { AgentAttachment } from '@commenda/types';
import dynamic from 'next/dynamic';
import { useCallback, useState, type ReactNode } from 'react';
import { ChatComposer } from './ChatComposer';
import { ChatDropZone } from './ChatDropZone';
import { useCommendaAiHost } from './CommendaAiHostProvider';
import {
  ComposerClientStrip,
  ComposerControlsHelper,
  ComposerFooterControls,
  type IComposerControlsConfig,
} from './composer-controls';
import { useCommendaAiWorkspace } from './useCommendaAiWorkspace';
import { useCreateCommendaAiConversation } from './useCreateCommendaAiConversation';

const ArmillaryBackdrop = dynamic(
  () => import('./ArmillaryBackdrop').then((m) => m.ArmillaryBackdrop),
  { ssr: false }
);

/** Starter prompts shown as chips under the composer (Figma: Chat with AI, node 171-13894).
 * Clicking one sends its `text` as the first message through the same create-on-send flow; `label`
 * lets a chip stay short while sending a longer message. */
const SUGGESTED_PROMPTS = [
  { label: 'Which filings are due in the next 30 days?' },
  { label: 'Which entities have GST or VAT returns coming up?' },
  { label: 'Which entities are missing compliance monitoring?' },
  {
    label: 'Onboard an entity',
    text: 'I want to onboard an already formed entity/entities by uploading documents.',
  },
];

/** Rounded chip buttons used for the suggested prompts here and for the client picks on the
 * affiliate chat's blank slate — one string so the two surfaces cannot drift apart. */
export const CHAT_CHIP_CLASS_NAMES =
  'cursor-pointer rounded-full bg-bg-secondary px-lg py-sm txt-small-regular text-text-primary transition-colors hover:bg-bg-secondary-hover';

interface NewChatScreenProps {
  /** Overrides the default chips: the affiliate console offers transfer pricing starters instead. */
  suggestedPrompts?: readonly { label: string; text?: string }[];
  conversationHref?: (conversationId: string) => string;
  /** Text the composer opens with, for a surface that routes here with a task in mind. It is only
   * pre-filled — the user still sends it. Omitted everywhere else, so the composer opens empty. */
  initialInput?: string;
  /** The client, agent, mode and model pickers this surface offers. A picked agent is the one the
   * created conversation runs as. Omitted, the composer offers only text and files. */
  composerControls?: IComposerControlsConfig;
  /** Rendered directly above the composer, like the conversation's own context slot. */
  contextSlot?: ReactNode;
  /** Replaces the built-in create. The workflow builder saves its canvas and resolves a target
   * version before a conversation can exist, so it owns that sequence and reports back. */
  onSubmit?: (text: string, attachments: AgentAttachment[]) => Promise<unknown>;
  /** Off for a surface whose run cannot take a file. Defaults to what the picked agent accepts. */
  allowAttachments?: boolean;
}

/** Blank-slate screen with no conversation selected. The conversation is created on the first send
 * and the page navigates to it — to `/agent?c=` by default, or wherever `conversationHref` points
 * for surfaces that live on another route (the affiliate console). */
export function NewChatScreen({
  conversationHref,
  initialInput = '',
  suggestedPrompts = SUGGESTED_PROMPTS,
  composerControls,
  contextSlot,
  onSubmit,
  allowAttachments = true,
}: NewChatScreenProps) {
  const { navigate } = useCommendaAiHost();
  const onCreated = useCallback(
    (conversationId: string) => {
      navigate(
        conversationHref?.(conversationId) ?? `/agent?c=${conversationId}`
      );
    },
    [navigate, conversationHref]
  );
  const profileKey = composerControls?.profilePicker?.profileKey ?? null;
  const { createConversation } = useCreateCommendaAiConversation({
    onCreated,
    profile: profileKey ?? undefined,
  });
  const { companyId } = useCommendaAiWorkspace();
  // Only the built-in create makes a chat conversation, the one agent that takes a data room.
  const isDataRoomChat =
    !onSubmit &&
    (profileKey === null || profileKey === COMMENDA_CHAT_PROFILE_KEY);
  // Files dropped anywhere over the blank screen, handed to the composer which owns the upload.
  const [droppedFiles, setDroppedFiles] = useState<
    CommendaAiUploadFile[] | null
  >(null);

  return (
    <ChatDropZone
      className="isolate h-full w-full overflow-hidden"
      onFilesDropped={setDroppedFiles}
    >
      <ArmillaryBackdrop />
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col items-center justify-center gap-10 px-4">
        <h1 className="text-center font-semibold text-2xl text-text-primary">
          Where should we begin?
        </h1>
        <div className="w-full">
          <div className="mb-3xl flex flex-wrap items-center justify-center gap-sm">
            {suggestedPrompts.map(({ label, text }) => (
              <button
                key={label}
                type="button"
                className={CHAT_CHIP_CLASS_NAMES}
                // The hook has already told the user when creation fails.
                onClick={() =>
                  (onSubmit
                    ? onSubmit(text ?? label, [])
                    : createConversation(text ?? label)
                  ).catch(() => undefined)
                }
              >
                {label}
              </button>
            ))}
          </div>
          <ComposerClientStrip composerControls={composerControls} />
          <ChatComposer
            autoFocus
            contextSlot={contextSlot}
            placeholder="Describe your task or ask a question"
            initialInput={initialInput}
            onSubmit={onSubmit ?? createConversation}
            allowAttachments={
              allowAttachments && profileTakesAttachments(profileKey)
            }
            dataRoomTarget={
              isDataRoomChat ? { conversationId: null, companyId } : undefined
            }
            showDisclaimer={false}
            filesToIngest={droppedFiles}
            onFilesIngested={() => setDroppedFiles(null)}
            toolsSlot={
              composerControls &&
              ComposerControlsHelper.hasFooterControls(composerControls) ? (
                <ComposerFooterControls composerControls={composerControls} />
              ) : undefined
            }
          />
        </div>
      </div>
    </ChatDropZone>
  );
}
